import type { LucideIcon } from 'lucide-react';
import { useEffect, useState, useSyncExternalStore } from 'react';

/**
 * O BANNER (v2.1, p.33; p.31; NO FLUXO desde a R06, p.67): avisa uma condição
 * que vale para a tela inteira e fica até ela acabar. Desde a R06 ele entra
 * entre a barra superior e o cabeçalho da página e empurra o conteúdo — nunca
 * cobre título, botões ou o primeiro cartão. Não é retorno de ação — esse é o toast
 * (`lib/retorno`) — nem recusa de formulário, que mora junto dos campos.
 *
 * As regras da p.33, e onde cada uma mora:
 *
 *  - **Um banner por vez. O mais grave fica.** Quem tem uma condição a avisar
 *    REGISTRA o banner dela aqui, e a tela mostra só `bannerDaVez()`: crítico
 *    antes de atenção, atenção antes de informativo; no empate, o que chegou
 *    primeiro (o que já está na tela não pisca para dar lugar a um igual).
 *  - **Os outros entram na central de avisos** (p.67): `bannersNaCentral()`
 *    devolve as condições registradas que não são a da vez — as que perderam
 *    a vez e as dispensadas que continuam valendo —, do mais grave ao menos.
 *  - **Fica até a condição acabar.** Quem registra é quem sabe quando ela
 *    acaba, e REMOVE o banner — a rede voltou, a lista zerou, a tela fechou.
 *  - **Crítico não fecha enquanto a condição existir.** `dispensarBanner` não
 *    faz nada com um crítico, e o componente não desenha o ✕ nele.
 *  - O não crítico, dispensado, fica dispensado NA SESSÃO do navegador
 *    (`sessionStorage`): recarregar a tela não o traz de volta, fechar o
 *    navegador traz. A chave é o `id` — uma condição NOVA (outra versão do
 *    sistema, outra contagem) registra outro `id` e aparece de novo.
 *
 * Uma loja só, para a mesa e para o celular: as duas cascas desenham o mesmo
 * estado, cada uma no seu lugar. Sem dependência — `useSyncExternalStore` é do
 * React, como em `lib/retorno`.
 */

export type Gravidade = 'critico' | 'atencao' | 'informativo';

/** A ordem da precedência: o menor vence. */
const PESO: Record<Gravidade, number> = { critico: 0, atencao: 1, informativo: 2 };

export interface AcaoDoBanner {
  /** Verbo + objeto, curto: "Recarregar", "Ver máquinas". */
  readonly rotulo: string;
  readonly aoAcionar: () => void;
}

export interface Banner {
  /** A condição. Outra condição, outro id: é ele que a dispensa lembra. */
  readonly id: string;
  readonly gravidade: Gravidade;
  /** A condição e o tamanho dela, com número (p.33). Frase completa. */
  readonly texto: string;
  /** Lucide, do vocabulário da skill `style` (p.19). */
  readonly icone: LucideIcon;
  readonly acao?: AcaoDoBanner;
  /** O nome acessível do ✕: "Fechar o aviso de nova versão". */
  readonly rotuloDeFechar?: string;
}

/** O bloco do ícone na tinta do tom (p.33): azul informa, âmbar pede atenção, vermelho é a falha. */
export const TOM_DO_BANNER: Record<Gravidade, { readonly bloco: string; readonly icone: string }> = {
  critico: { bloco: 'bg-erro-fundo', icone: 'text-erro' },
  atencao: { bloco: 'bg-atencao-fundo', icone: 'text-atencao' },
  informativo: { bloco: 'bg-informacao-fundo', icone: 'text-informacao' },
};

const CHAVE_DA_SESSAO = 'ct-frotas:banners-dispensados';

let registrados: readonly (Banner & { readonly ordem: number })[] = [];
let proximaOrdem = 0;
let dispensados: ReadonlySet<string> = lerDispensados();
let daVez: Banner | null = null;
let naCentral: readonly Banner[] = [];
const ouvintes = new Set<() => void>();

/* O armazenamento da sessão LEVANTA em navegador com dado de site bloqueado,
   em vez de devolver nulo; a dispensa é conveniência e nunca derruba a tela. */
function lerDispensados(): ReadonlySet<string> {
  try {
    const bruto = globalThis.sessionStorage?.getItem(CHAVE_DA_SESSAO);
    const lista: unknown = bruto ? JSON.parse(bruto) : [];
    return new Set(Array.isArray(lista) ? lista.filter((x): x is string => typeof x === 'string') : []);
  } catch {
    return new Set();
  }
}

function guardarDispensados(): void {
  try {
    globalThis.sessionStorage?.setItem(CHAVE_DA_SESSAO, JSON.stringify([...dispensados]));
  } catch {
    /* sem armazenamento: a dispensa vale até recarregar */
  }
}

/** O banner que a tela mostra: o mais grave dos registrados e não dispensados. */
export function escolherBanner(
  candidatos: readonly (Banner & { readonly ordem: number })[],
  dispensa: ReadonlySet<string>,
): Banner | null {
  let melhor: (Banner & { readonly ordem: number }) | null = null;
  for (const b of candidatos) {
    if (b.gravidade !== 'critico' && dispensa.has(b.id)) continue;
    if (
      melhor === null ||
      PESO[b.gravidade] < PESO[melhor.gravidade] ||
      (PESO[b.gravidade] === PESO[melhor.gravidade] && b.ordem < melhor.ordem)
    ) {
      melhor = b;
    }
  }
  return melhor;
}

/**
 * O que vai à central de avisos (p.67): todo registrado que não é o da vez,
 * do mais grave ao menos e, no empate, o que chegou primeiro.
 */
export function separarDaCentral(
  candidatos: readonly (Banner & { readonly ordem: number })[],
  vez: Banner | null,
): readonly Banner[] {
  return candidatos
    .filter((b) => b.id !== vez?.id)
    .sort((a, b) => PESO[a.gravidade] - PESO[b.gravidade] || a.ordem - b.ordem);
}

function avisar(): void {
  daVez = escolherBanner(registrados, dispensados);
  naCentral = separarDaCentral(registrados, daVez);
  for (const o of ouvintes) o();
}

/**
 * Registra a condição. O mesmo `id` de novo troca o texto e a ação sem perder
 * o lugar na fila do empate.
 */
export function registrarBanner(banner: Banner): void {
  const existente = registrados.find((b) => b.id === banner.id);
  const ordem = existente ? existente.ordem : proximaOrdem++;
  registrados = [...registrados.filter((b) => b.id !== banner.id), { ...banner, ordem }];
  avisar();
}

/** A condição acabou. */
export function removerBanner(id: string): void {
  if (!registrados.some((b) => b.id === id)) return;
  registrados = registrados.filter((b) => b.id !== id);
  avisar();
}

/**
 * Quem leu fecha. Crítico não fecha (p.33): devolve `false` e nada muda.
 */
export function dispensarBanner(id: string): boolean {
  const banner = registrados.find((b) => b.id === id);
  if (!banner || banner.gravidade === 'critico') return false;
  dispensados = new Set([...dispensados, id]);
  guardarDispensados();
  avisar();
  return true;
}

export function bannerDaVez(): Banner | null {
  return daVez;
}

/** As condições que não estão no banner e vão à central de avisos (p.67). */
export function bannersNaCentral(): readonly Banner[] {
  return naCentral;
}

/** Para o teste: começa do zero, sem registro e sem dispensa. */
export function limparBanners(): void {
  registrados = [];
  dispensados = new Set();
  guardarDispensados();
  avisar();
}

function assinar(o: () => void): () => void {
  ouvintes.add(o);
  return () => {
    ouvintes.delete(o);
  };
}

export function useBannerDaVez(): Banner | null {
  return useSyncExternalStore(assinar, bannerDaVez, bannerDaVez);
}

/** As condições da central de avisos (p.67), para o sino desenhar. */
export function useBannersNaCentral(): readonly Banner[] {
  return useSyncExternalStore(assinar, bannersNaCentral, bannersNaCentral);
}

/**
 * Mantém a condição registrada enquanto o componente está na tela e ela
 * existe (`banner` não nulo); remove quando ela acaba ou a tela sai.
 *
 * O banner é comparado pelo que se vê — id, texto e rótulo da ação —, e não
 * pela identidade do objeto: quem chama monta um objeto novo a cada desenho.
 */
export function useBanner(banner: Banner | null): void {
  const id = banner?.id ?? null;
  const assinatura = banner ? `${banner.gravidade}|${banner.texto}|${banner.acao?.rotulo ?? ''}` : null;
  useEffect(() => {
    if (banner === null || id === null) return;
    registrarBanner(banner);
    return () => {
      removerBanner(id);
    };
    // O objeto muda a cada desenho; o que importa é o que ele mostra.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, assinatura]);
}

/**
 * O banner que a casca desenha agora, com a saída da p.21: quando a vez passa
 * para outro (ou para nenhum), o que estava na tela SAI primeiro — sobe os
 * 8 px e se apaga — e só depois o seguinte entra. `aoTerminarAnimacao` vai no
 * `onAnimationEnd` do cartão.
 */
export function useBannerNaTela(): {
  readonly banner: Banner | null;
  readonly saindo: boolean;
  readonly aoTerminarAnimacao: () => void;
} {
  const daVez = useBannerDaVez();
  const [mostrado, setMostrado] = useState<Banner | null>(daVez);
  const [saindo, setSaindo] = useState(false);
  /* Ajuste durante o desenho (e não em efeito): o React refaz o desenho na
     hora, sem um quadro com o banner velho e o estado novo. */
  if (daVez?.id === mostrado?.id) {
    if (daVez !== mostrado) setMostrado(daVez);
    if (saindo) setSaindo(false);
  } else if (mostrado === null) {
    setMostrado(daVez);
  } else if (!saindo) {
    setSaindo(true);
  }
  return {
    banner: mostrado,
    saindo,
    aoTerminarAnimacao: () => {
      if (!saindo) return;
      setSaindo(false);
      setMostrado(bannerDaVez());
    },
  };
}
